import {CommentsCategory, type CommentDto, getCommentLastModified} from '@domain/comments'
import type {BaseSchedule, ScheduleItem} from '@domain/schedule'
import type {AssetSnapshot} from '@hconnect/common/types'
import {useUpmPlantTimezone} from '@hconnect/upm'
import moment, {Moment} from 'moment-timezone'
import {useMemo} from 'react'

import {useAssetCommentsQuery} from './useAssetCommentsQuery'
import {useCommentsPermissions} from './useCommentsPermissions'
import {useScheduleCommentsQuery} from './useScheduleCommentsQuery'

interface UsePlanningCommentsParams {
  schedule: BaseSchedule | undefined
  assets: AssetSnapshot[] | undefined
  scheduleItemRange: [Moment, Moment]
}

export const usePlanningComments = ({
  schedule,
  assets,
  scheduleItemRange: [startOfSelection, endOfSelection]
}: UsePlanningCommentsParams) => {
  const timezoneId = useUpmPlantTimezone()

  const scheduleItems: ScheduleItem[] = useMemo(
    () => Object.values(schedule?.schedules ?? {}),
    [schedule]
  )

  const scheduleItemIds = useMemo(() => scheduleItems.map(({id}) => id), [scheduleItems])
  const selectedScheduleItemIds = useMemo(
    () =>
      scheduleItems
        .filter(
          (scheduleItem) =>
            moment.utc(scheduleItem.start).tz(timezoneId).isBefore(endOfSelection) &&
            moment.utc(scheduleItem.end).tz(timezoneId).isAfter(startOfSelection)
        )
        .map(({id}) => id),
    [scheduleItems, startOfSelection, endOfSelection, timezoneId]
  )

  const {canViewComments: canViewAssetComments} = useCommentsPermissions(CommentsCategory.Assets)
  const {canViewComments: canViewScheduleComments} = useCommentsPermissions(
    CommentsCategory.AssetOperationTimes
  )
  const {data: assetComments, isInitialLoading: areAssetCommentsLoading} = useAssetCommentsQuery(
    assets?.map((asset) => asset.id),
    !canViewAssetComments
  )
  const {data: scheduleComments, isInitialLoading: areScheduleCommentsLoading} =
    useScheduleCommentsQuery({
      scheduleItemIds,
      isDisabled: !canViewScheduleComments,
      selectedScheduleItemIds
    })
  const isLoadingComments = areAssetCommentsLoading || areScheduleCommentsLoading

  const comments = useMemo(() => {
    if (isLoadingComments) {
      return undefined
    }

    const allComments: CommentDto[] = [...(assetComments ?? []), ...(scheduleComments ?? [])]
    const sortedComments = allComments.sort((a, b) => {
      const aDate = getCommentLastModified(a).date
      const bDate = getCommentLastModified(b).date
      if (aDate < bDate) return 1
      if (aDate > bDate) return -1
      return 0
    })

    return sortedComments
  }, [assetComments, scheduleComments, isLoadingComments])

  return {
    comments,
    isLoading: isLoadingComments
  }
}
